import { useQuery } from "@tanstack/react-query";
import { from, rpc, unwrap } from "./rpc";

/** Leituras do `core` que toda UI de módulo usa (todo módulo depende do core). */

export interface IssuerRow {
  id: string;
  org_id: string;
  legal_name: string;
  trade_name: string | null;
  cnpj: string;
  municipal_registration: string | null;
  municipality_ibge: string | null;
  tax_regime: "mei" | "simples" | "presumido" | "real";
  active: boolean;
}

export function useIssuers(orgId: string) {
  return useQuery({
    queryKey: ["core", "issuers", orgId],
    queryFn: () => unwrap<IssuerRow[]>(from("core", "issuers").select("*").eq("org_id", orgId).order("legal_name")),
  });
}

export function issuerLabel(i: Pick<IssuerRow, "trade_name" | "legal_name"> | undefined | null): string {
  return i ? (i.trade_name || i.legal_name) : "";
}

export interface PersonLite {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  company_name: string | null;
}

/** Nomes de pessoas por id (para tabelas de outros módulos que só guardam person_id). */
export function usePeople(orgId: string, ids: Array<string | null | undefined>) {
  const uniq = [...new Set(ids.filter((x): x is string => Boolean(x)))].sort();
  return useQuery({
    queryKey: ["core", "people", "lite", orgId, uniq],
    enabled: uniq.length > 0,
    queryFn: async () => {
      const rows = await unwrap<PersonLite[]>(
        from("core", "people").select("id, full_name, email, phone, company_name").eq("org_id", orgId).in("id", uniq),
      );
      return new Map(rows.map((r) => [r.id, r]));
    },
  });
}

/** Busca de pessoas por nome/e-mail (seletor de pessoa e paleta ctrl+k). */
export async function searchPeople(orgId: string, text: string, limit = 8): Promise<PersonLite[]> {
  const s = text.replace(/[,()"*\\%]/g, " ").trim();
  if (!s) return [];
  return unwrap<PersonLite[]>(
    from("core", "people")
      .select("id, full_name, email, phone, company_name")
      .eq("org_id", orgId)
      .or(`full_name.ilike."*${s}*",email.ilike."*${s}*",company_name.ilike."*${s}*"`)
      .order("full_name")
      .limit(limit),
  );
}

export interface MemberRow {
  user_id: string;
  email: string | null;
  role: string;
  role_label: string;
  rank: number;
  created_at: string;
  is_me: boolean;
}

export function useMembers(orgId: string, enabled = true) {
  return useQuery({
    queryKey: ["core", "members", orgId],
    enabled,
    queryFn: async () => ((await rpc("core", "list_members", { p_org: orgId })) ?? []) as MemberRow[],
  });
}

/** Leitor de auditoria para o AuditTimeline (@vixio/ui), com e-mail de quem fez. */
export async function loadAudit(q: { orgId: string; entity?: string; entityId?: string; limit?: number }) {
  let b = from("core", "audit_log").select("*").eq("org_id", q.orgId).order("at", { ascending: false }).limit(q.limit ?? 50);
  if (q.entity) b = b.eq("entity", q.entity);
  if (q.entityId) b = b.eq("entity_id", q.entityId);
  const rows = await unwrap<Array<{ id: number; org_id: string | null; actor: string | null; action: string; entity: string; entity_id: string | null; data: Record<string, unknown>; at: string }>>(b);
  let emails = new Map<string, string>();
  try {
    const members = ((await rpc("core", "list_members", { p_org: q.orgId })) ?? []) as MemberRow[];
    emails = new Map(members.filter((m) => m.email).map((m) => [m.user_id, m.email!]));
  } catch {
    /* sem permissão de listar membros: mostra sem e-mail */
  }
  return rows.map((r) => ({ ...r, actor_email: r.actor ? (emails.get(r.actor) ?? null) : null }));
}
